import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Users, MapPin, Zap } from "lucide-react";
import { useSite } from "@/i18n/SiteContext";
import { tr } from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";
import { nearestTown } from "@/lib/geo";
import { basePriceEuro, durationLabel, participantsLabel, priceUnitLabel, isPerGroup } from "@/lib/pricing";

export const TourCard = ({ tour, index = 0 }) => {
  const { lang, t } = useSite();
  const c = tr(tour, lang);
  const perGroup = isPerGroup(tour);
  return (
    <Reveal delay={(index % 3) * 0.08} y={24}>
      <Link to={`/tours/${tour.slug}`} className="block group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sea rounded-2xl" data-testid={`tour-card-${tour.slug}`}>
        <div className="img-zoom aspect-[4/3] rounded-2xl overflow-hidden shadow-soft group-hover:shadow-soft-lg bg-sand relative" style={{ transition: "box-shadow .3s ease" }}>
          <img src={tour.image} alt={c.title} loading="lazy" className="w-full h-full object-cover" />
          <div className="absolute top-4 left-4 flex flex-wrap gap-2">
            <span className="bg-ivory text-ocean text-[11px] font-medium tracking-wider uppercase px-3 py-1.5 rounded-full shadow-soft">{t(`cat.${tour.category}`)}</span>
            <span className="bg-ocean/85 text-ivory text-[11px] font-medium tracking-wider uppercase px-3 py-1.5 rounded-full shadow-soft" data-testid={`tour-card-type-${tour.slug}`}>{t(`type.${tour.tour_type || "group"}`)}</span>
          </div>
          {tour.skip_the_line === "yes" && (
            <span className="absolute bottom-4 left-4 inline-flex items-center gap-1.5 bg-sand-brand text-ocean text-[11px] font-medium tracking-wider uppercase px-3 py-1.5 rounded-full shadow-soft" data-testid={`tour-card-skipline-${tour.slug}`}>
              <Zap size={12} /> {t("facts.skipLine")}
            </span>
          )}
        </div>
        <div className="mt-5">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3 text-xs font-sans text-ocean/55">
            <span className="inline-flex items-center gap-1.5"><MapPin size={13} />{nearestTown(tour.lat, tour.lng)}</span>
            <span className="inline-flex items-center gap-1.5"><Clock size={13} />{durationLabel(tour, t)}</span>
            <span className="inline-flex items-center gap-1.5"><Users size={13} />{participantsLabel(tour, t)}</span>
          </div>
          <h3 className="font-serif text-2xl text-ocean group-hover:text-sea leading-snug" style={{ transition: "color .3s ease" }}>
            {c.title}
          </h3>
          <p className="mt-2 text-sm font-sans text-ocean/60 leading-relaxed line-clamp-2">{c.short}</p>
          <div className="mt-4 flex items-end justify-between gap-3">
            <p className="text-sm font-sans text-ocean/60 leading-tight">
              {!perGroup && <>{t("card.from")} </>}
              <span className="font-serif text-2xl text-ocean" data-testid={`tour-card-price-${tour.slug}`}>{basePriceEuro(tour)}</span>
              <span className="block text-xs mt-0.5" data-testid={`tour-card-price-unit-${tour.slug}`}>{priceUnitLabel(tour, t)}</span>
            </p>
            <span className="flex items-center gap-2 text-sm font-medium text-sea shrink-0">
              {t("card.discover")}
              <ArrowRight size={14} className="group-hover:translate-x-1" style={{ transition: "transform .3s ease" }} />
            </span>
          </div>
        </div>
      </Link>
    </Reveal>
  );
};
